import { apiClient } from '@littlebird/auth-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';

import { useRefetchWhenTurnEnds } from './refetch-when-turn-ends';

const requestSchema = z.object({
  request_id: z.string(),
  chat_id: z.string(),
  source_domain: z.string(),
  credential_id: z.string(),
  destination_origin: z.string(),
  fields: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      type: z.enum(['text', 'email', 'password', 'unknown']),
      is_credential_id: z.boolean(),
    })
  ),
  sections: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        choices: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
            saved_credential: z.boolean().optional(),
          })
        ),
      })
    )
    .default([]),
  status: z.enum(['pending', 'resolved', 'expired']),
  result: z.object({ action: z.string() }).nullish(),
  notified: z.boolean(),
});

export type SavedCredentialRequest = z.infer<typeof requestSchema>;
const chatKey = (chatId: string) =>
  ['saved-credential-request', chatId] as const;
const requestKey = (requestId: string) =>
  ['saved-credential-request-id', requestId] as const;

/** The user still has to answer this request, or the answer has not reached the agent */
export const isSavedRequestOpen = (
  request: SavedCredentialRequest | null | undefined
) =>
  request?.status === 'pending' ||
  (request?.status === 'resolved' && !request.notified);

/**
 * The chat's own saved credential request. Polls while a turn runs, since a
 * turn may raise one at any time, and while a raised request is still open.
 */
export const useChatSavedCredentialRequestQuery = (
  chatId: string,
  enabled: boolean,
  chatBusy: boolean
) => {
  useRefetchWhenTurnEnds(chatKey(chatId), chatBusy);
  return useQuery({
    queryKey: chatKey(chatId),
    queryFn: async ({ signal }) =>
      z.object({ request: requestSchema.nullable() }).parse(
        await apiClient.get('browser/saved_credential_requests', {
          params: { chat_id: chatId },
          signal,
          emitHttpErrorEvent: false,
        })
      ).request,
    enabled,
    refetchInterval: ({ state: { data } }) =>
      chatBusy || isSavedRequestOpen(data) ? 5_000 : false,
    retry: false,
  });
};

export const useSavedCredentialRequestQuery = (requestId: string) =>
  useQuery({
    queryKey: requestKey(requestId),
    queryFn: async ({ signal }) =>
      requestSchema.parse(
        await apiClient.get(
          `browser/saved_credential_requests/${encodeURIComponent(requestId)}`,
          {
            signal,
            emitHttpErrorEvent: false,
          }
        )
      ),
    refetchInterval: 5_000,
    retry: false,
  });

type ResponseBody =
  | { action: 'approve' | 'reject' }
  | { action: 'choose'; section_id: string; choice_id: string };

export const useSavedCredentialActions = (request: SavedCredentialRequest) => {
  const client = useQueryClient();
  const [state, setState] = useState({ isPending: false, isError: false });
  const refresh = async () =>
    Promise.all([
      client.invalidateQueries({ queryKey: chatKey(request.chat_id) }),
      client.invalidateQueries({ queryKey: requestKey(request.request_id) }),
    ]);
  const path = `browser/saved_credential_requests/${encodeURIComponent(request.request_id)}`;
  const submit = async (
    body: ResponseBody,
    beforeSubmit?: () => void | Promise<void>
  ) => {
    setState({ isPending: true, isError: false });
    try {
      await beforeSubmit?.();
      // Blob bodies are not serialized into the API client's error telemetry.
      const response = await apiClient.fetch(`${path}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: new Blob([JSON.stringify(body)], { type: 'application/json' }),
      });
      if (!response.ok) throw new Error('Could not send response');
      await refresh();
      setState({ isPending: false, isError: false });
    } catch {
      setState({ isPending: false, isError: true });
    }
  };
  const resume = async () => {
    setState({ isPending: true, isError: false });
    try {
      await apiClient.post(`${path}/resume`, undefined, {
        emitHttpErrorEvent: false,
      });
      await refresh();
      setState({ isPending: false, isError: false });
    } catch {
      setState({ isPending: false, isError: true });
    }
  };
  return { ...state, submit, resume };
};
